import { ref } from 'vue';
import { ComposerTranslation } from 'vue-i18n';

interface ChipStyle {
  backgroundColor: string;
  fontColor: string;
}

interface StatusChipData {
  statusId: number;
  tooltipText: (t: ComposerTranslation) => string | undefined;
  style: ChipStyle;
}

export enum SendStatusId {
  SENT = 1,
  DELV = 2,
  RECI = 3,
  NRE1 = 4,
  REMI = 5,
  NRE2 = 6,
  FWTR = 7,
  FWTC = 8,
  SENT2 = 9,
  ERROR = 10,
}

export const SendMenuPaths = {
  SendMonitorFood: '/send/send-food',
  SendMonitorNonFood: '/send/send-non-food',
} as const;

// key: statusId, value: statusShort
export const SendStatusIdToSendStatusShortMap = new Map<number, string>([
  [1, 'SENT'],
  [2, 'DELV'],
  [3, 'RECI'],
  [4, 'NRE1'],
  [5, 'REMI'],
  [6, 'NRE2'],
  [7, 'FWTR'],
  [8, 'FWTC'],
  [9, 'SENT2'],
  [10, 'ERROR'],
]);

const sendStatusMapEntries: [number, StatusChipData][] = [
  [
    SendStatusId.SENT,
    {
      statusId: SendStatusId.SENT,
      tooltipText: (t) => t('tooltips.sendStatus.sent'),
      style: { backgroundColor: '#B2D6F0', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.DELV,
    {
      statusId: SendStatusId.DELV,
      tooltipText: (t) => t('tooltips.sendStatus.delv'),
      style: { backgroundColor: '#0CC2CE', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.RECI,
    {
      statusId: SendStatusId.RECI,
      tooltipText: (t) => t('tooltips.sendStatus.reci'),
      style: { backgroundColor: '#72CB94', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.NRE1,
    {
      statusId: SendStatusId.NRE1,
      tooltipText: (t) => t('tooltips.sendStatus.nre1'),
      style: { backgroundColor: '#0C4D7A', fontColor: '#FFFFFF' },
    },
  ],
  [
    SendStatusId.REMI,
    {
      statusId: SendStatusId.REMI,
      tooltipText: (t) => t('tooltips.sendStatus.remi'),
      style: { backgroundColor: '#0079CE', fontColor: '#FFFFFF' },
    },
  ],
  [
    SendStatusId.NRE2,
    {
      statusId: SendStatusId.NRE2,
      tooltipText: (t) => t('tooltips.sendStatus.nre2'),
      style: { backgroundColor: '#0C4D7A', fontColor: '#FFFFFF' },
    },
  ],
  [
    SendStatusId.FWTR,
    {
      statusId: SendStatusId.FWTR,
      tooltipText: (t) => t('tooltips.sendStatus.fwtr'),
      style: { backgroundColor: '#D2D2D2', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.FWTC,
    {
      statusId: SendStatusId.FWTC,
      tooltipText: (t) => t('tooltips.sendStatus.fwtc'),
      style: { backgroundColor: '#D2D2D2', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.SENT2,
    {
      statusId: SendStatusId.SENT2,
      tooltipText: (t) => t('tooltips.sendStatus.sent2'),
      style: { backgroundColor: '#B2D6F0', fontColor: '#464646' },
    },
  ],
  [
    SendStatusId.ERROR,
    {
      statusId: SendStatusId.ERROR,
      tooltipText: (t) => t('tooltips.sendStatus.error'),
      style: { backgroundColor: '#E42912', fontColor: '#FFFFFF' },
    },
  ],
] as const;

export const SendStatusChipDataMap = new Map<number, StatusChipData>(sendStatusMapEntries);

export enum CommTypeToId {
  EMAIL = 1,
  MFT = 2,
  EDI = 4,
}

export enum YesNoFlagId {
  YES = 1,
  NO = 2,
}

export const PrimaryContactFilterValues = ref([
  {
    primaryContactId: YesNoFlagId.YES,
    primaryContactShort: 'Ja',
    primaryContactDesc: 'Ja',
  },
  {
    primaryContactId: YesNoFlagId.NO,
    primaryContactShort: 'Nein',
    primaryContactDesc: 'Nein',
  },
]);

export const CommTypeFilterValues = ref([
  {
    commTypeId: CommTypeToId.EDI,
    commTypeShort: 'EDI',
    commTypeDesc: 'EDI',
  },
  {
    commTypeId: CommTypeToId.EMAIL,
    commTypeShort: 'Email',
    commTypeDesc: 'E-Mail',
  },
  {
    commTypeId: CommTypeToId.MFT,
    commTypeShort: 'MFT',
    commTypeDesc: 'MFT',
  },
]);

export const NotificationFilterValues = ref([
  {
    notificationId: YesNoFlagId.YES,
    notificationShort: 'Ja',
    notificationDesc: 'Ja',
  },
  {
    notificationId: YesNoFlagId.NO,
    notificationShort: 'Nein',
    notificationDesc: 'Nein',
  },
]);

export const TableTabs = { order: 'tab-order', hauler: 'tab-hauler' } as const;
export const SidebarTabs = { details: 'tab-details', history: 'tab-history' } as const;

// key: tableTab, value: messageTypeId
export const TableTabToMessageTypeIdMap = new Map<string, number>([
  [TableTabs.order, 1],
  [TableTabs.hauler, 2],
]);

export enum SendMonitorTableColumnField {
  SELECTION = 'selection',
  STATUS_ID = 'statusId',
  DOCUMENT_ID = 'documentId',
  COUNTRY = 'country',
  LOGISTIC_TYPE_SHORT = 'logisticTypeShort',
  DISPO_WEEK_FROM = 'dispoWeekFrom',
  DISPO_WEEK_UNTIL = 'dispoWeekUntil',
  BUSINESS_PARTNER_ID = 'businessPartnerId',
  BUSINESS_PARTNER_NAME = 'businessPartnerName',
  BUSINESS_PARTNER_LOCATION = 'businessPartnerLocation',
  SUBJECT = 'subject',
  PRIMARY_CONTACT = 'primaryContact',
  COMM_TYPE_ID = 'commTypeId',
  CONTACT_NAME = 'contactName',
  PARENT_COMM_TYPE_NAME = 'parentCommTypeName',
  EMAIL = 'email',
  COMM_TYPE = 'commType',
  DISPO_ID = 'dispoId',
  ORDER_ID = 'orderId',
  SEND_DATE = 'sendDate',
  DELIVERY_DATE = 'deliveryDate',
  TASK_ID = 'taskId',
  TRANSFER_FILE_ID = 'transferfileId',
  EXISTSNOTIFICATIONS = 'existsNotifications',
  NOTIFICATION_IDS = 'notificationIds',
}

export interface SendMonitorTableColumnOrderNumbers {
  [SendMonitorTableColumnField.SELECTION]: number;
  [SendMonitorTableColumnField.STATUS_ID]: number;
  [SendMonitorTableColumnField.DOCUMENT_ID]: number;
  [SendMonitorTableColumnField.COUNTRY]: number;
  [SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT]: number;
  [SendMonitorTableColumnField.DISPO_WEEK_FROM]: number;
  [SendMonitorTableColumnField.DISPO_WEEK_UNTIL]: number;
  [SendMonitorTableColumnField.EMAIL]: number;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_NAME]: number;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_ID]: number;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION]: number;
  [SendMonitorTableColumnField.SUBJECT]: number;
  [SendMonitorTableColumnField.PRIMARY_CONTACT]: number;
  [SendMonitorTableColumnField.COMM_TYPE]: number;
  [SendMonitorTableColumnField.DISPO_ID]: number;
  [SendMonitorTableColumnField.ORDER_ID]: number;
  [SendMonitorTableColumnField.SEND_DATE]: number;
  [SendMonitorTableColumnField.DELIVERY_DATE]: number;
  [SendMonitorTableColumnField.TASK_ID]: number;
  [SendMonitorTableColumnField.TRANSFER_FILE_ID]: number;
  [SendMonitorTableColumnField.EXISTSNOTIFICATIONS]: number;
  [key: string]: number;
}

export interface SendMonitorTableColumnVisibilities {
  [SendMonitorTableColumnField.COUNTRY]: boolean;
  [SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT]: boolean;
  [SendMonitorTableColumnField.DISPO_WEEK_FROM]: boolean;
  [SendMonitorTableColumnField.DISPO_WEEK_UNTIL]: boolean;
  [SendMonitorTableColumnField.EMAIL]: boolean;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_NAME]: boolean;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_ID]: boolean;
  [SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION]: boolean;
  [SendMonitorTableColumnField.SUBJECT]: boolean;
  [SendMonitorTableColumnField.PRIMARY_CONTACT]: boolean;
  [SendMonitorTableColumnField.COMM_TYPE]: boolean;
  [SendMonitorTableColumnField.DISPO_ID]: boolean;
  [SendMonitorTableColumnField.ORDER_ID]: boolean;
  [SendMonitorTableColumnField.SEND_DATE]: boolean;
  [SendMonitorTableColumnField.DELIVERY_DATE]: boolean;
  [SendMonitorTableColumnField.TASK_ID]: boolean;
  [SendMonitorTableColumnField.EXISTSNOTIFICATIONS]: boolean;
  [SendMonitorTableColumnField.TRANSFER_FILE_ID]: boolean;
  [key: string]: boolean;
}

export const OrderTabDefaultColumnVisibilities: SendMonitorTableColumnVisibilities = {
  [SendMonitorTableColumnField.COUNTRY]: true,
  [SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT]: true,
  [SendMonitorTableColumnField.DISPO_WEEK_FROM]: true,
  [SendMonitorTableColumnField.DISPO_WEEK_UNTIL]: true,
  [SendMonitorTableColumnField.EMAIL]: false,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_NAME]: true,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_ID]: false,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION]: false,
  [SendMonitorTableColumnField.SUBJECT]: false,
  [SendMonitorTableColumnField.PRIMARY_CONTACT]: true,
  [SendMonitorTableColumnField.COMM_TYPE]: true,
  [SendMonitorTableColumnField.DISPO_ID]: true,
  [SendMonitorTableColumnField.ORDER_ID]: true,
  [SendMonitorTableColumnField.SEND_DATE]: true,
  [SendMonitorTableColumnField.DELIVERY_DATE]: true,
  [SendMonitorTableColumnField.TASK_ID]: false,
  [SendMonitorTableColumnField.EXISTSNOTIFICATIONS]: false,
  [SendMonitorTableColumnField.TRANSFER_FILE_ID]: false,
};

export const HaulerTabDefaultColumnVisibilities: SendMonitorTableColumnVisibilities = {
  [SendMonitorTableColumnField.COUNTRY]: true,
  [SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT]: true,
  [SendMonitorTableColumnField.DISPO_WEEK_FROM]: true,
  [SendMonitorTableColumnField.DISPO_WEEK_UNTIL]: true,
  [SendMonitorTableColumnField.EMAIL]: false,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_NAME]: true,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_ID]: false,
  [SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION]: false,
  [SendMonitorTableColumnField.SUBJECT]: false,
  [SendMonitorTableColumnField.PRIMARY_CONTACT]: false,
  [SendMonitorTableColumnField.COMM_TYPE]: false,
  [SendMonitorTableColumnField.DISPO_ID]: true,
  [SendMonitorTableColumnField.ORDER_ID]: false,
  [SendMonitorTableColumnField.SEND_DATE]: true,
  [SendMonitorTableColumnField.DELIVERY_DATE]: true,
  [SendMonitorTableColumnField.TASK_ID]: false,
  [SendMonitorTableColumnField.EXISTSNOTIFICATIONS]: false,
  [SendMonitorTableColumnField.TRANSFER_FILE_ID]: false,
};

export const SendMonitorTableColumnsWithChangeableVisibility = [
  SendMonitorTableColumnField.COUNTRY,
  SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT,
  SendMonitorTableColumnField.DISPO_WEEK_FROM,
  SendMonitorTableColumnField.DISPO_WEEK_UNTIL,
  SendMonitorTableColumnField.EMAIL,
  SendMonitorTableColumnField.BUSINESS_PARTNER_NAME,
  SendMonitorTableColumnField.BUSINESS_PARTNER_ID,
  SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION,
  SendMonitorTableColumnField.SUBJECT,
  SendMonitorTableColumnField.PRIMARY_CONTACT,
  SendMonitorTableColumnField.COMM_TYPE,
  SendMonitorTableColumnField.DISPO_ID,
  SendMonitorTableColumnField.ORDER_ID,
  SendMonitorTableColumnField.SEND_DATE,
  SendMonitorTableColumnField.DELIVERY_DATE,
  SendMonitorTableColumnField.TASK_ID,
  SendMonitorTableColumnField.EXISTSNOTIFICATIONS,
  SendMonitorTableColumnField.TRANSFER_FILE_ID,
];

// key: field, value: orderNumber
export const SendMonitorTableColumnFieldToOrderNumberMap = new Map<string, number>([
  [SendMonitorTableColumnField.SELECTION, 0],
  [SendMonitorTableColumnField.STATUS_ID, 1],
  [SendMonitorTableColumnField.DOCUMENT_ID, 2],
  [SendMonitorTableColumnField.EXISTSNOTIFICATIONS, 3],
  [SendMonitorTableColumnField.COUNTRY, 4],
  [SendMonitorTableColumnField.LOGISTIC_TYPE_SHORT, 5],
  [SendMonitorTableColumnField.DISPO_WEEK_FROM, 6],
  [SendMonitorTableColumnField.DISPO_WEEK_UNTIL, 7],
  [SendMonitorTableColumnField.BUSINESS_PARTNER_ID, 8],
  [SendMonitorTableColumnField.BUSINESS_PARTNER_NAME, 9],
  [SendMonitorTableColumnField.BUSINESS_PARTNER_LOCATION, 10],
  [SendMonitorTableColumnField.SUBJECT, 11],
  [SendMonitorTableColumnField.PRIMARY_CONTACT, 12],
  [SendMonitorTableColumnField.EMAIL, 13],
  [SendMonitorTableColumnField.COMM_TYPE, 14],
  [SendMonitorTableColumnField.DISPO_ID, 15],
  [SendMonitorTableColumnField.ORDER_ID, 16],
  [SendMonitorTableColumnField.SEND_DATE, 17],
  [SendMonitorTableColumnField.DELIVERY_DATE, 18],
  [SendMonitorTableColumnField.TASK_ID, 19],
  [SendMonitorTableColumnField.TRANSFER_FILE_ID, 20],
]);
